import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

const FAQS = [
  {
    q: "Is the strategy call really free?",
    a: "Yes. It's a 15-minute conversation with our team focused on your situation. There's nothing to buy on the call itself — the goal is to help you identify the most practical next move for your business.",
  },
  {
    q: "Who is this best for?",
    a: "Business owners who already have some lead flow or a service to sell and want lead generation, follow-up, and automation working as one system rather than as disconnected tools.",
  },
  {
    q: "Do I need to be technical?",
    a: "No. We'll speak in plain business language on the call. Any tools or automations we discuss are chosen based on what fits your team — not the other way around.",
  },
  {
    q: "What should I bring to the call?",
    a: "A short honest description of how leads reach you today and where follow-up is breaking down. That's it. No slides or prep documents required.",
  },
  {
    q: "Will I be pressured to buy something?",
    a: "No. The call is a real conversation. If we're not the right fit, we'll say so. If we are, we'll walk through the options and you decide on your own timeline.",
  },
  {
    q: "What happens after I book?",
    a: "You'll get a calendar invite by email with the meeting link and a short pre-call note. On the call we'll listen first, then share the highest-leverage next step for your business.",
  },
];

export default function ReplayFaq() {
  const [open, setOpen] = useState(0);
  return (
    <section
      id="faq"
      className="relative py-28 md:py-40 border-t wam-hairline"
      data-testid="replay-faq"
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-10">
        <div className="mb-10 flex items-center gap-4">
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#F0ECE4]/40">
            [006] —
          </span>
          <span className="font-mono text-[11px] tracking-[0.3em] text-[#C9A84C]">
            COMMON QUESTIONS
          </span>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="font-display max-w-[900px] mb-16"
          style={{ fontSize: "clamp(30px, 4vw, 60px)", lineHeight: 0.98, letterSpacing: "-0.03em" }}
        >
          Straight <span className="text-[#C9A84C]">answers.</span>
        </motion.h2>

        <ul>
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <li key={f.q} className="border-t wam-hairline">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="group flex w-full items-center gap-6 py-6 md:py-7 text-left transition-colors hover:bg-white/[0.02] focus:outline-none focus-visible:bg-white/[0.03]"
                  data-testid={`replay-faq-toggle-${i}`}
                >
                  <span className="font-mono text-[11px] tracking-[0.24em] text-[#C9A84C] w-8 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex-1 font-display text-[17px] md:text-[22px] tracking-[-0.02em]">
                    {f.q}
                  </span>
                  <motion.span
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="font-display text-[#C9A84C] text-[24px] leading-none shrink-0"
                  >
                    +
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pl-[3.5rem] pr-10 pb-8 font-body text-[15px] leading-[1.8] text-[#F0ECE4]/75 max-w-[720px]">
                        {f.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
          <li className="border-t wam-hairline" />
        </ul>
      </div>
    </section>
  );
}
